/** Общие кусочки UI «WB: регионы»: ошибка API, форматы (null — «нет данных», 0 — «0»), бейдж
 *  класса дефицита строки витрины, фото оффера. */
import React, { useState } from "react";

import type { DeficitCounts } from "@/api/wbRegions";
import { cn } from "@/lib/utils";
import { int, pct } from "../ozon-analytics/format";
import {
  dec1,
  DEFICIT_STYLE,
  isWeakConfidence,
  NO_DATA,
  NO_DEMAND,
  ORDERS_INCOMPLETE,
  SALES_INCOMPLETE,
} from "./format";
import { apiErrorText } from "../ozon-analytics/ui";

export { Loading, Seg } from "../ozon-analytics/shared";

export function ErrorBox({ error, what }: { error: unknown; what: string }): React.ReactElement {
  return (
    <div
      role="alert"
      data-testid="wb-error"
      className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
    >
      Не удалось получить {what}. {apiErrorText(error, "«WB: регионы»")}
    </div>
  );
}


/** Серый текст «нет данных» / «нет спроса» вместо числа. */
export function Missing({ text = NO_DATA, title }: { text?: string; title?: string }): React.ReactElement {
  return (
    <span className="text-muted-foreground" title={title} data-testid="missing">
      {text}
    </span>
  );
}

/** Штуки: «1 234», 0 — «0», null — серым «нет данных». */
export function Qty({ v }: { v: number | null }): React.ReactElement {
  return v === null ? <Missing /> : <>{int(v)}</>;
}


export function PerDay({ v }: { v: number | null }): React.ReactElement {
  return v === null ? <Missing /> : <>{dec1(v)}</>;
}

/** Дни покрытия: null при нулевом спросе — «нет спроса», иначе — «нет данных». */
export function Days({
  v,
  demand,
}: {
  v: number | null;
  demand: number | null;
}): React.ReactElement {
  if (v !== null) return <>{dec1(v)}</>;
  return demand === 0 ? <Missing text={NO_DEMAND} /> : <Missing />;
}



/** Уверенность FBO-оценки: < 0,8 — серым курсивом; подсказка — разбивка по способам. */
export function Confidence({
  v,
  title,
}: {
  v: number | null;
  title: string;
}): React.ReactElement {
  if (v === null) return <Missing text="—" title={`FBO 0 — доли нет. ${title}`} />;
  return (
    <span
      title={title}
      data-testid="confidence"
      className={cn("cursor-help", isWeakConfidence(v) && "italic text-muted-foreground")}
    >
      {pct(v)}
    </span>
  );
}





const COUNT_ORDER: (keyof DeficitCounts)[] = ["zero", "low", "ok", "excess", "no_demand"];

/** Счётчики строк витрины по классу (агрегат класса не имеет): цвет — у каждого счётчика своего
 *  класса, ноль — без цвета. */
export function DeficitCountsView({ counts }: { counts: DeficitCounts }): React.ReactElement {
  return (
    <span className="inline-flex flex-wrap justify-end gap-1" data-testid="deficit-counts">
      {COUNT_ORDER.map((k) => {
        const n = counts[k];
        const label = k === "no_demand" ? NO_DEMAND : DEFICIT_STYLE[k].label;
        return (
          <span
            key={k}
            title={`${label}: ${n} строк оффер × регион`}
            data-class={k}
            className={cn(
              "rounded px-1 text-[11px] tabular-nums",
              n === 0 ? "text-muted-foreground/60" : k === "no_demand" ? "text-muted-foreground" : DEFICIT_STYLE[k].cls,
              n > 0 && k === "ok" && "border",
            )}
          >
            {label} {n}
          </span>
        );
      })}
    </span>
  );
}

/** Фото оффера WB: нет — подпись, не загрузилось — подпись. */
export function OfferPhoto({
  url,
  alt,
  size = "small",
}: {
  url: string | null;
  alt: string;
  size?: "small" | "large";
}): React.ReactElement {
  const [failed, setFailed] = useState(false);
  const box = size === "small" ? "size-10" : "size-24";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/30",
        box,
      )}
    >
      {url && !failed ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className={cn(box, "object-contain")}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="px-0.5 text-center text-[9px] leading-tight text-muted-foreground">
          {url ? "фото не загрузилось" : "нет фото"}
        </span>
      )}
    </div>
  );
}

/** Пометка неполного дня спроса/продаж строки витрины (правило 7). */
export function IncompleteMark({
  orders,
  sales,
}: {
  orders: boolean | null;
  sales: boolean | null;
}): React.ReactElement | null {
  const parts = [orders === false && ORDERS_INCOMPLETE, sales === false && SALES_INCOMPLETE].filter(Boolean);
  if (!parts.length) return null;
  return (
    <span
      className="ml-1 cursor-help text-amber-600 dark:text-amber-400"
      title={`День неполный: ${parts.join(", ")}`}
      data-testid="incomplete-day"
    >
      ⚠
    </span>
  );
}
